import React from 'react';
import { Link } from 'react-router-dom';

const SECTIONS = [
  {
    id: 'special-orders',
    title: 'SPECIAL ORDERS AND MADE-TO-ORDER PRODUCTS',
    blocks: [
      {
        heading: 'Important Notice',
        body: 'Most of our products are considered special order or made to order items. These items are defined as:',
        list: [
          'Built to order',
          'Customized or one-offs',
          'Non-refundable from the manufacturer',
          'Outside the bounds of normal stock',
        ],
      },
      {
        heading: 'Terms and Conditions',
        list: [
          'Once the invoice or deposit is paid, special order items cannot be cancelled or returned for any reason.',
          'No changes can be made once the order is placed, so please review specifications carefully.',
          'All sales on special order items are final.',
          'Please note that some items may not be intended for use with overseas vehicle variants.',
        ],
      },
      {
        heading: 'Product Guarantee',
        list: [
          'We guarantee that all products we sell are genuine and in new condition, unless explicitly marked as blemished or used.',
          "We cannot guarantee the exact fit of imported products on vehicles of different origins than the manufacturer's intended use.",
          'Your order is not conditional upon guaranteed fitment.',
        ],
      },
      {
        heading: 'Dispute Resolution',
        list: [
          'Any disputes regarding incorrect product fulfillment must be filed within five days of product delivery and must be accompanied by written documentation supporting the claim.',
          'In cases of potential miscommunication, we will contact our customers to verify order details.',
        ],
      },
      {
        heading: 'Lead Times and Delays',
        list: [
          'Estimated lead times provided for special orders are estimates only.',
          'Delays can occur, and a delay in manufacturing or fulfillment is not a valid reason to cancel.',
          'We cannot accept responsibility for delays beyond our control and will not incur damages due to such delays.',
        ],
      },
      {
        heading: 'Shipping Estimates and Charges',
        list: [
          'You will be provided a shipping estimate at the time of the invoice.',
          'Shipping charges can fluctuate regularly and sometimes drastically.',
          'While we make every reasonable effort to provide an accurate estimate based on current rates, fluctuations in shipping costs are not a valid reason to cancel.',
          'The final shipping charges can only be calculated once the item is in our warehouse.',
        ],
      },
      {
        heading: 'Inspecting Shipments',
        list: [
          'Special order parts must be inspected immediately upon receipt.',
          'In the event of a freight delivery, the packaging should be photographed, opened, and all components inspected and documented prior to signing for the parts and release of the driver.',
          'For normal deliveries (UPS, FedEx, DHL, etc.), packaging should be photographed, and products should be inspected immediately.',
          'Failure to notify us with documentation of a damaged shipment within 24 hours will result in the contracted carrier denying insurance claims.',
        ],
      },
    ],
  },
  {
    id: 'refunds-exchanges',
    title: 'REFUNDS AND EXCHANGES',
    blocks: [
      {
        heading: 'Special Order Policy',
        list: [
          'Most of our products are made on a pre-paid, made-to-order basis and are considered "special orders".',
          'All special orders must be paid in full prior to starting the order.',
          'Special orders are non-refundable.',
        ],
      },
      {
        heading: 'Refund Policy',
        list: [
          'Customer is required to contact Fortune Automotive Design Inc. if they wish to receive a refund for their purchase.',
          'Any claim submissions and/or chargebacks filed without contacting Fortune Automotive Design Inc. will be considered fraudulent and will be subject to legal action.',
          'Due to the fact that our company is a smaller scale operation, refunds will be issued at our discretion and within our determined timelines \u2014 typically within 15-30 day(s) \u2014 we will always do whatever we can to reach an agreement that is satisfactory between all parties.',
          'Refunds will be subject to a minimum cancellation 30% fee to cover production and/or restocking costs of the original order.',
        ],
      },
      {
        heading: 'Exchange and Return Policy',
        list: [
          'No exchange or return will be allowed or accepted without written confirmation from the management of Fortune Automotive Design Inc.',
          'Please contact us to obtain written approval before returning any products.',
        ],
      },
    ],
  },
  {
    id: 'carbon-fiber',
    title: 'CARBON FIBER & FIBERGLASS',
    blocks: [
      {
        heading: 'Important Notice',
        body: 'Please note the following conditions for carbon fiber and fiberglass parts:',
        list: [
          'No Implied Warranty: There is no implied warranty on visual defects for these parts.',
          'Handmade Products: Handmade carbon fiber products may have visual defects, including weave pull, weave distortion, visible seams, discoloration, pinholes, and sanding marks.',
          'Preparation and Finishing: Unless explicitly stated, many carbon fiber and fiberglass parts require preparation, painting, or clear coating by an experienced professional.',
          'Warranty Claims: By ordering this product, you understand and agree that any warranty claims are at the sole discretion of the manufacturer.',
        ],
      },
    ],
  },
  {
    id: 'pricing',
    title: 'PRICING',
    blocks: [
      {
        heading: 'Pricing Disclaimer',
        body: 'While we strive to provide accurate pricing, please note that:',
        list: [
          'Prices are entered manually and may not reflect real-time exchange rate fluctuations.',
          'We cannot always honor the original displayed price due to volatile changes in international exchange rates, supplier prices, shipping costs, import duties, and other factors.',
          'Prices may change without notice, and any inconsistencies do not entitle customers to compensation or damages.',
        ],
      },
      {
        heading: 'Order Confirmation',
        body: 'To ensure accuracy, we recommend contacting us before placing your online order to:',
        list: [
          'Confirm product availability',
          'Verify current pricing',
        ],
      },
    ],
  },
  {
    id: 'international',
    title: 'INTERNATIONAL ORDERS',
    blocks: [
      {
        heading: 'Global Shipping',
        body: 'At Fortune Automotive Design Inc., we proudly ship our products worldwide.',
      },
      {
        heading: 'Payment Requirements',
        body: 'For customers outside of North America, please note:',
        list: [
          'Initial orders must be paid by credit card or bank wire.',
        ],
      },
      {
        heading: 'Import Taxes, Fees, and Duties',
        list: [
          'Customers are responsible for any taxes, fees, and duties imposed by their country on imported items.',
          'We cannot estimate or cover these costs, as they vary by country and region.',
        ],
      },
      {
        heading: 'Customs and Import Liabilities',
        list: [
          'As the customer, you are entirely responsible for any customs duties, charges, or liabilities associated with international shipping.',
          'We recommend checking with your local authorities to understand potential costs and regulations.',
        ],
      },
    ],
  },
  {
    id: 'shipping-damage',
    title: 'SHIPPING AND DAMAGE',
    blocks: [
      {
        heading: 'Inspection and Packaging',
        list: [
          'Many products are carefully inspected for damage prior to shipping.',
          'Some items are marked as "DNR" (do-not-repack) and are not inspected.',
          'We reinforce original packaging provided by the manufacturer to minimize potential shipping damage.',
        ],
      },
      {
        heading: 'Delivery and Inspection',
        list: [
          'Upon arrival, inspect everything thoroughly, even if packaging is intact, before signing the delivery receipt.',
          'If the box is damaged, take photos and notate the damage when receiving the item.',
          'If you cannot officially notate damage with the shipping carrier at delivery, reject the package.',
        ],
      },
      {
        heading: 'Damage Claims',
        list: [
          'Once signed as undamaged, freight damage claims are null and void.',
          'Items cannot be insured for more than the stated value.',
          'Action must be taken within 24 hours of delivery to process a damage claim.',
          'After 24 hours, refunds, reorders, and claims cannot be processed.',
        ],
      },
      {
        heading: 'Insurance and Liability',
        list: [
          'All shipped items are fully insured.',
          'Fortune Automotive Design Inc. will assist with claims but is not responsible for the outcome.',
        ],
      },
    ],
  },
  {
    id: 'shipping-info',
    title: 'SHIPPING AND ORDER DELIVERY INFORMATION',
    blocks: [
      {
        heading: 'Special Order Delivery',
        list: [
          'Special order items from overseas take longer, with delivery times varying greatly.',
          'We provide delivery estimates based on manufacturer availability and shipping schedules, but these are only estimates and not guaranteed.',
          'Manufacturers often work on a "just in time" inventory system, and production times vary greatly by item.',
          'We ship a minimum of one full shipment each month from our suppliers.',
        ],
      },
      {
        heading: 'International Shipping',
        body: 'International ocean freight times vary based on several stages:',
        list: [
          'Overseas paperwork and customs',
          'Ocean transit',
          'US customs and clearance',
          'Port congestion can add significant delays to orders.',
        ],
      },
      {
        heading: 'Delivery Estimates',
        list: [
          'Due to various manufacturer and shipping schedules, delivery estimates are only estimates and not guaranteed.',
          'We aim to estimate conservatively and usually deliver faster than our estimates suggest.',
          'By ordering, you acknowledge that there is no required delivery ETA, and orders cannot be canceled or altered due to manufacturing or shipping delays.',
          'All ETAs provided are best guesses only.',
        ],
      },
    ],
  },
  {
    id: 'competition-offroad',
    title: 'COMPETITION, OFFROAD USE ONLY, AND LOCAL VEHICLE REGULATIONS',
    blocks: [
      {
        heading: 'Intended Use',
        list: [
          'Some parts are designed for competition and/or off-highway applications only.',
          'These parts are not intended for use on public highways.',
        ],
      },
      {
        heading: 'Legal Compliance',
        list: [
          'Federal and state laws prohibit modifying or removing emission or safety devices on vehicles used on public highways.',
          'Violating these laws may result in fines or penalties.',
          "Installing these parts may void your vehicle's warranty.",
        ],
      },
      {
        heading: 'Responsibility',
        list: [
          'Fortune Automotive Design Inc. sells products for off-road use only and takes no responsibility for individual client use.',
          'We have no knowledge of or responsibility for informing you of local road or off-road usage laws.',
          'All products may or may not be legal for road or track use in your local jurisdiction.',
        ],
      },
    ],
  },
  {
    id: 'data-disclaimer',
    title: 'INCORRECT OR MISSING DATA DISCLAIMER',
    blocks: [
      {
        heading: 'Pricing and Data Errors',
        list: [
          'Fortune Automotive Design Inc. reserves the right to nullify and void any orders containing incorrect or missing product pricing, shipping pricing, or other data.',
          'This includes, but is not limited to, errors on our website.',
        ],
      },
      {
        heading: 'Order Cancellation and Refund',
        list: [
          'We reserve the right to cancel any order with incorrect or wrong pricing.',
          'A full refund will be issued to the customer at the earliest convenience.',
        ],
      },
    ],
  },
  {
    id: 'arbitration',
    title: 'DISPUTES / ARBITRATION',
    blocks: [
      {
        heading: 'Payment Responsibility',
        body: 'By placing your order, you agree to be responsible for full payment.',
      },
      {
        heading: 'Dispute Resolution',
        body: 'You agree not to file payment disputes and to cover any associated costs. In the event of disputes, you agree to:',
        list: [
          'Submit the issue to a non-binding mediator.',
          'If mediation fails, submit to an arbitrator.',
          "The arbitrator's decision will be binding on both parties.",
          'Any mediator or arbitrator must be a neutral party acceptable to both you and Fortune Automotive Design Inc.',
          'The losing party will cover the costs of mediation or arbitration.',
          "All mediations/arbitrations will be held at Fortune Automotive Design Inc.'s registered headquarters.",
        ],
      },
    ],
  },
  {
    id: 'product-warranty',
    title: 'PRODUCT WARRANTY',
    blocks: [
      {
        heading: 'Terms and Conditions',
        body: 'By placing an order with Fortune Automotive Design Inc. and its affiliates, you acknowledge and accept all conditions listed on this page.',
      },
      {
        heading: 'Dispute Resolution',
        body: 'If you have any questions or wish to clarify or dispute any of these policies, you must do so in writing before placing an order.',
      },
      {
        heading: 'Policy Changes',
        body: 'These policies and procedures are subject to change at any time without notice.',
      },
    ],
  },
  {
    id: 'copyrights',
    title: 'COPYRIGHTS',
    blocks: [
      {
        heading: 'License to Use Intellectual Property',
        list: [
          "Fortune Automotive Design Inc.'s product photos, descriptions, and other website content are protected by copyright.",
          'To use our intellectual property, a yearly license fee of: $100 per year per photo, $100 per year per product description.',
          'Partial year usage will be billed on a prorated basis.',
          'Fees may be waived in writing on a case-by-case basis at the discretion of Fortune Automotive Design Inc.',
        ],
      },
      {
        heading: 'Terms of Use',
        list: [
          'Using our photos, logos, text, or other materials constitutes agreement with this policy.',
          'By using our intellectual property, you waive any rights to contest the fee schedule.',
        ],
      },
    ],
  },
];

const Terms = () => {
  return (
    <div className="bg-white text-neutral-900">
      {/* Hero */}
      <section className="pt-36 md:pt-32 pb-12 border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">LEGAL</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight">TERMS AND CONDITIONS</h1>
          <p className="text-neutral-600 mt-5 max-w-2xl">
            Please read these terms carefully before placing an order. By purchasing from Fortune Auto Design, you acknowledge and accept the conditions outlined below.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[260px_1fr] gap-10">
          {/* Sticky TOC */}
          <aside className="lg:sticky lg:top-28 lg:self-start hidden lg:block">
            <div className="font-display tracking-[0.22em] text-[11px] text-neutral-500 mb-4">CONTENTS</div>
            <ul className="space-y-2 text-sm border-l border-neutral-200">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="block pl-4 -ml-px border-l border-transparent hover:border-[#FF6B00] hover:text-[#FF6B00] py-1 text-neutral-700 transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Body */}
          <article className="max-w-[760px]">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="mb-14 scroll-mt-28">
                <h2 className="font-display text-2xl md:text-3xl tracking-tight border-b border-neutral-200 pb-3 mb-6">
                  {s.title}
                </h2>
                {s.blocks.map((b, i) => (
                  <div key={i} className="mb-7">
                    {b.heading && (
                      <h3 className="font-display tracking-[0.18em] text-sm font-bold text-[#FF6B00] mb-3">
                        {b.heading.toUpperCase()}
                      </h3>
                    )}
                    {b.body && (
                      <p className="text-neutral-700 leading-relaxed mb-3">{b.body}</p>
                    )}
                    {b.list && (
                      <ul className="space-y-2">
                        {b.list.map((item, j) => (
                          <li key={j} className="text-neutral-700 leading-relaxed pl-5 relative">
                            <span className="absolute left-0 top-2 w-1.5 h-1.5 bg-[#FF6B00]" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </section>
            ))}

            <div className="mt-10 p-6 bg-neutral-50 border border-neutral-200">
              <p className="text-sm text-neutral-600">
                If you have any questions regarding these terms, please <Link to="/contact" className="text-[#FF6B00] hover:underline">contact us</Link> before placing an order.
              </p>
              <p className="text-xs text-neutral-500 mt-3">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default Terms;
